import { EDevice } from '@/constants';
import type { BrazeClient } from '@/Infrastructure/Utils/analytics/braze/braze.types.ts';
import { notifyError } from '@/modules/perseo-billing/error-handler';
import { getDeviceFromUrl } from '@/modules/perseo-billing/utils';
import { validateDeviceReadiness } from '@/modules/shared/utils/validateDeviceReadiness';

declare global {
  interface Window {
    braze?: BrazeClient;
  }
}

export async function trackEvent(
  name: string,
  attrs?: Record<string, unknown>
): Promise<void> {
  try {
    if (!window.braze) {
      console.error('[Braze] Braze not initialized');
      return;
    }

    const device = getDeviceFromUrl();

    if (!device || device === EDevice.WEB || device === 'kiosco') {
      window.braze.logCustomEvent(name, attrs);
      return;
    }

    try {
      validateDeviceReadiness(device);
      if (device === EDevice.IOS) return sendIosEvent(name, attrs);
      if (device === EDevice.ANDROID) return sendAndroidEvent(name, attrs);
    } catch (error) {
      notifyError(error);
      console.error(
        `[Braze] Device not ready for tracking event: ${name}`,
        error
      );
    }
  } catch (err) {
    console.error(`[Braze] Failed to track event: ${name}`, err);
  }
}

export async function changeUser(userId?: string | null): Promise<void> {
  if (!userId || !window?.braze) return;

  try {
    window?.braze?.changeUser?.(userId);
  } catch (err) {
    console.error('[Braze] Failed to change user', err);
  }
}

export async function setUserContactData(
  email?: string,
  phone?: string
): Promise<void> {
  try {
    if (!window?.braze) return;

    const user = window.braze.getUser?.();
    if (!user) return;

    if (email && user.setEmail) {
      user.setEmail(email);
    }

    if (phone && user.setPhoneNumber) {
      user.setPhoneNumber(phone);
    }
  } catch (error) {
    console.error('[Braze] setUserContactData failed', error);
  }
}

export async function addDeviceAlias(): Promise<void> {
  try {
    if (!window?.braze) return;

    const braze = window.braze;
    const user = braze.getUser?.();
    if (!user) return;

    braze.getDeviceId?.((deviceId: string) => {
      if (!deviceId) return;

      user.addAlias?.(deviceId, 'device_alias');

      braze.requestImmediateDataFlush?.();
    });
  } catch (error) {
    console.error('[Braze] addDeviceAlias failed', error);
  }
}

const sendIosEvent = (name: string, attrs?: Record<string, unknown>): void => {
  const message = {
    command: 'logEvent',
    name: name,
    parameters: attrs || {}
  };

  window?.webkit?.messageHandlers?.firebase?.postMessage(message);
};

const sendAndroidEvent = (
  name: string,
  attrs?: Record<string, unknown>
): void => {
  window?.AnalyticsWebInterface?.logEvent(name, JSON.stringify(attrs));
};
